import { FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import {
  parseShortcutCommand,
  shouldOpenCommandBar,
  SHORTCUT_COMMAND_PLACEHOLDER,
  type ShortcutAction
} from './shortcuts';

interface ShortcutCommandBarProps {
  open: boolean;
  disabled?: boolean;
  availableSymbols: readonly string[];
  onOpen: () => void;
  onClose: () => void;
  onExecute: (action: ShortcutAction) => void;
}

const INITIAL_VALUE = '';

export function ShortcutCommandBar({
  open,
  disabled = false,
  availableSymbols,
  onOpen,
  onClose,
  onExecute
}: ShortcutCommandBarProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);

  const [value, setValue] = useState(INITIAL_VALUE);
  const [error, setError] = useState<string>();

  const closeCommandBar = useCallback(() => {
    setValue(INITIAL_VALUE);
    setError(undefined);
    onClose();
  }, [onClose]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (disabled) return;

      if (open) {
        if (event.key === 'Escape') {
          event.preventDefault();
          event.stopPropagation();
          closeCommandBar();
        }

        return;
      }

      if (!shouldOpenCommandBar(event)) return;

      event.preventDefault();
      event.stopPropagation();

      setValue(INITIAL_VALUE);
      setError(undefined);
      onOpen();
    };

    window.addEventListener('keydown', handleKeyDown, true);

    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [disabled, open, onOpen, closeCommandBar]);

  useEffect(() => {
    if (disabled && open) {
      closeCommandBar();
    }
  }, [disabled, open, closeCommandBar]);

  useEffect(() => {
    if (!open) return;

    setValue(INITIAL_VALUE);
    setError(undefined);

    const frame = window.requestAnimationFrame(() => {
      inputRef.current?.focus();
    });

    return () => window.cancelAnimationFrame(frame);
  }, [open]);

  if (!open) return null;

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const result = parseShortcutCommand(value, {
      availableSymbols
    });

    if (!result.ok) {
      setError(result.message);
      return;
    }

    onExecute(result.action);
    closeCommandBar();
  };

  return (
    <div className="shortcut-command">
      <button
        className="shortcut-command__dismiss"
        type="button"
        aria-label="Close command bar"
        onClick={closeCommandBar}
      />

      <form className="shortcut-command__bar" onSubmit={submit}>
        <label className="shortcut-command__label" htmlFor="shortcut-command-input">
          Command
        </label>

        <input
          ref={inputRef}
          id="shortcut-command-input"
          className="shortcut-command__input"
          value={value}
          spellCheck={false}
          autoComplete="off"
          autoCapitalize="off"
          aria-describedby={error ? 'shortcut-command-error' : undefined}
          placeholder={SHORTCUT_COMMAND_PLACEHOLDER}
          onChange={(event) => {
            setValue(event.target.value);
            setError(undefined);
          }}
        />

        {error ? (
          <span id="shortcut-command-error" className="shortcut-command__error">
            {error}
          </span>
        ) : null}
      </form>
    </div>
  );
}